'use client';

import { Sparkles, Box } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { ProBadge } from '@/features/subscription/ui/ProBadge';
import { SettingsCard, PickerRow } from '@/shared/ui';
import { useSubscriptionLimits } from '@/features/subscription';
import { usePassPaywallStore } from '@/shared/stores/pass-paywall-store';
import type { RenderModelFamily } from '@/types/render';

export interface ModelContentProps {
  selectedModelFamily: RenderModelFamily;
  onModelFamilyChange: (family: RenderModelFamily) => void;
  // Layout variant — kept for API parity; both variants share the Apple-grouped
  // row idiom now.
  variant?: 'default' | 'compact';
  closeOnSelect?: () => void;
}

export function ModelContent({
  selectedModelFamily,
  onModelFamilyChange,
  closeOnSelect,
}: ModelContentProps) {
  const t = useTranslations('render.settings.model');
  // ATLAS is a Pro-tier model. Lite/Guest users still see the row so they
  // understand what the tier unlocks.
  const limits = useSubscriptionLimits();
  const hasModelSelection = limits?.has_model_selection ?? false;
  // Named model selection (ATLAS) is a Pro perk (moved Designer → Pro
  // 2026-05-27; EntitlementFeatureMap: model_selection). A locked tap upsells
  // the Pro Pass (paywall opens with the Pro disclosure pre-expanded) — never
  // the deprecated subscription paywall.
  const openPassPaywall = usePassPaywallStore((s) => s.open);

  const models: Array<{
    type: RenderModelFamily;
    icon: typeof Sparkles;
    isPro?: boolean;
  }> = [
    {
      type: 'AURORA',
      icon: Sparkles,
    },
    {
      type: 'ATLAS',
      icon: Box,
      isPro: true,
    },
  ];

  const handleSelect = (model: (typeof models)[number]) => {
    if (model.isPro && !hasModelSelection) {
      openPassPaywall('pro');
      return;
    }
    onModelFamilyChange(model.type);
    closeOnSelect?.();
  };

  return (
    <div className="space-y-4">
      {/* Apple ModelPickerView parity: grouped card with leading icon + title +
          PRO badge + description + trailing checkmark. No section footer —
          Apple dropped the "Choose the rendering model" footer text. */}
      <SettingsCard>
        {models.map((model) => {
          const isSelected = selectedModelFamily === model.type;
          const Icon = model.icon;
          const isLocked = model.isPro === true && !hasModelSelection;

          const trailing = model.isPro ? (
            <span className="flex items-center gap-1.5">
              <ProBadge tier="pro" isLocked={!hasModelSelection} />
            </span>
          ) : undefined;

          const leading = (
            <Icon className="h-5 w-5 text-primary" strokeWidth={1.5} />
          );

          return (
            <PickerRow
              key={model.type}
              title={t(`types.${model.type}.name`)}
              description={t(`types.${model.type}.description`)}
              leading={leading}
              trailing={trailing}
              isSelected={isSelected && !isLocked}
              // Locked row stays tappable (not `isDisabled`) so the tap opens
              // the Pro Pass paywall — mirrors Apple, where the locked model
              // row routes to the paywall instead of being inert. The ProBadge
              // lock icon conveys the locked state visually.
              onClick={() => handleSelect(model)}
            />
          );
        })}
      </SettingsCard>
    </div>
  );
}

// Helper to get subtitle for preview
export function getModelSubtitle(
  selectedModelFamily: RenderModelFamily,
  t: (key: string) => string
): string {
  return t(`types.${selectedModelFamily}.name`);
}
